import React, { useEffect, useState } from 'react';
import { useWorldStore } from '../../state/worldStore';
import { useRideGate } from '../../hooks/useRideGate';
import { IS_GUEST } from '../../config';
import { api } from '../../services/api';
import { INTRO_RIDE } from '../../worldRide';

// Rides — the ride's diegetic home.
//
// The intro's ride is SPOR1'S (Build_Game_Intro §4.2): the three anonymous
// operators collapsed into one named canon interviewer — host of Decoded,
// driving you in himself because Lyra called ahead. He does three jobs and
// only one is data: the tour (the map tutorial), the promise (the central
// reveal), and the interview (the bio engine). "Do not skip this for anyone."
//
// The old flavour rides (the 47, Hyper, the limo) stay listed but off tonight:
// an operator with no drivers reads as a city that exists beyond what you can
// do in it, and they return as open-city rides after the come-up.
//
// Which VARIANT plays — the interview or the homecoming — is the server's
// call (GET /api/dialogue/ride): canon, not a client preference.

const SPOR1 = {
  // The yellow Lotus — the city's own parked Elise, the car every other
  // surface (Lyra's call, the beacon, the kerb card) already calls it.
  label: 'Spor1', driver: 'Spor1', vehicle: "Yellow Lotus — you can't miss it",
  eta: '2 min', fare: 'Lyra called ahead', accent: '#ffb25e',
  note: 'Host of Decoded. Tonight, personally, your ride. Ask him anything; he will ask first.',
};

const OFF_TONIGHT = [
  { key: 'act2_ride_bus', label: 'The 47', vehicle: 'AC Transit · 47 Foothill', note: "Not running tonight — Spor1's got you." },
  { key: 'act2_ride_hyper', label: 'Hyper', vehicle: 'Silver Charger · 7KDR841', note: 'No drivers nearby.' },
  { key: 'act2_ride_limo', label: 'The car', vehicle: 'Black Escalade · no plate', note: 'Nobody sent one. Yet.' },
];

const DESTINATION = { name: 'The Salon', street: 'Go get right first — the scenic way' };

export default function PhoneRides() {
  const myAvatarId = useWorldStore((s) => s.myAvatarId);
  const openScene = useWorldStore((s) => s.openScene);
  const startRide = useWorldStore((s) => s.startRide);
  const setPhoneOpen = useWorldStore((s) => s.setPhoneOpen);
  const setPhoneApp = useWorldStore((s) => s.setPhoneApp);
  // THE SAME GATE as the kerb card, the beacon and tapping Spor1 (useRideGate).
  // This app used to keep its own quest fetch, which (a) never loaded for a
  // GUEST — "Looking for drivers…" forever — (b) let a member hail before
  // Lyra had even rung, and (c) went stale, so after the ride it said "take
  // the call first" to somebody who had just finished it.
  const { arrivalDone, rideOpen } = useRideGate();
  const phoneQuests = useWorldStore((s) => s.phoneQuests);
  const guestProgress = useWorldStore((s) => s.guestProgress);
  const loaded = IS_GUEST || (phoneQuests || []).length > 0;
  const rideDone = IS_GUEST ? !!guestProgress?.rideDone
    : arrivalDone && !(phoneQuests || []).some((q) => q.slug === 'come_up_the_ride');

  const [hailed, setHailed] = useState(false);
  const [arrived, setArrived] = useState(false);

  // The ride unlocks behind the call — before Lyra rings, you have no reason
  // to be in anybody's car. Saying that in fiction beats a greyed button.
  const locked = loaded && !rideOpen && !rideDone;

  // The car takes a moment to turn up. Short — the ride itself is the content,
  // and a wait screen is exactly the spinner design law 3 forbids.
  useEffect(() => {
    if (!hailed) { setArrived(false); return undefined; }
    const t = setTimeout(() => setArrived(true), 2200);
    return () => clearTimeout(t);
  }, [hailed]);

  useEffect(() => {
    if (!arrived || !hailed) return undefined;
    const t = setTimeout(async () => {
      // The server picks the variant (interview vs homecoming). If it can't be
      // asked, the interview is the safe default — a stranger's first night.
      let key = 'act2_ride_spor1';
      let title = "Spor1's ride";
      // A guest always gets the interview (there is no catalogue to come home
      // to), and has no quest rows — the same shape GuestChatDock opens.
      if (!IS_GUEST) {
        try {
          const d = await api.dialogueRide(myAvatarId);
          if (d?.scene) { key = d.scene; title = d.title || title; }
        } catch { /* default stands */ }
      }
      // Hand the conversation to SceneDock, the camera to RideSequence, and
      // get out of the way — you're in the car now, not looking at your phone.
      openScene({ key, title, quest: IS_GUEST ? null : 'come_up_the_ride', room: null });
      startRide({ sceneKey: key, pace: INTRO_RIDE.pace });
      setPhoneOpen(false);
      setPhoneApp(null);
      setHailed(false);
    }, 900);
    return () => clearTimeout(t);
  }, [arrived]); // eslint-disable-line react-hooks/exhaustive-deps

  if (hailed) {
    return (
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', padding: '24px 20px', textAlign: 'center', gap: 16 }}>
        <div style={{
          width: 78, height: 78, borderRadius: '50%', display: 'grid', placeItems: 'center',
          background: `linear-gradient(160deg, ${SPOR1.accent}, rgba(255,94,120,0.85))`,
          color: '#1a0f00', font: '800 30px system-ui',
        }}>S</div>
        <div>
          <div style={{ font: '800 19px system-ui' }}>{SPOR1.driver}</div>
          <div style={{ font: '600 11.5px system-ui', opacity: 0.55, marginTop: 3 }}>{SPOR1.vehicle}</div>
        </div>
        <div style={{ font: '700 13.5px system-ui', color: SPOR1.accent }}>
          {arrived ? 'He\'s outside' : `Arriving · ${SPOR1.eta}`}
        </div>
        {!arrived && (
          <button onClick={() => setHailed(false)} style={{
            marginTop: 6, padding: '8px 18px', borderRadius: 999, cursor: 'pointer',
            border: '1px solid rgba(255,233,196,0.3)', background: 'none',
            color: 'rgba(255,233,196,0.8)', font: '700 12px system-ui',
          }}>Cancel</button>
        )}
      </div>
    );
  }

  return (
    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '10px 16px 16px' }}>
      <div style={{
        borderRadius: 14, padding: '11px 13px', marginBottom: 14,
        background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,233,196,0.16)',
      }}>
        <div style={{ font: '600 10px system-ui', opacity: 0.5, letterSpacing: 0.6 }}>WHERE TO</div>
        <div style={{ font: '700 15px system-ui', marginTop: 3 }}>
          {!loaded ? '—' : locked ? 'No destination set' : rideDone ? 'You made it' : DESTINATION.name}
        </div>
        <div style={{ font: '600 11.5px system-ui', opacity: 0.55 }}>
          {!loaded ? ' ' : locked ? 'Nobody has told you where to be yet.' : rideDone ? 'Spor1 dropped you off. Walk it from here.' : DESTINATION.street}
        </div>
      </div>

      {/* Held until the quest list lands: offering a ride and then pulling it
          a beat later reads as a bug, not as a locked door. */}
      {!loaded && (
        <div style={{ font: '600 12px system-ui', opacity: 0.4, textAlign: 'center', marginTop: 24 }}>
          Looking for drivers…
        </div>
      )}

      {locked && (
        <div style={{ font: '600 12px system-ui', opacity: 0.5, lineHeight: 1.6, textAlign: 'center', marginTop: 24 }}>
          Take the call first.<br />You can't tell a driver an address you don't have.
        </div>
      )}

      {loaded && rideDone && (
        <div style={{ font: '600 12px system-ui', opacity: 0.6, lineHeight: 1.6, textAlign: 'center', marginTop: 24 }}>
          No rides booked.<br />Everything you need tonight is a short walk — check the map.
        </div>
      )}

      {loaded && !locked && !rideDone && (
        <button onClick={() => setHailed(true)} style={rideCard(SPOR1.accent)}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8 }}>
            <span style={{ font: '700 14.5px system-ui' }}>{SPOR1.label}</span>
            <span style={{ font: '700 12px system-ui', color: SPOR1.accent }}>{SPOR1.eta}</span>
          </div>
          <div style={{ font: '600 11px system-ui', opacity: 0.55, marginTop: 2 }}>{SPOR1.vehicle}</div>
          <div style={{ font: '600 11.5px system-ui', opacity: 0.75, marginTop: 6, lineHeight: 1.4 }}>{SPOR1.note}</div>
          <div style={{ font: '700 10.5px system-ui', opacity: 0.5, marginTop: 6, letterSpacing: 0.3 }}>{SPOR1.fare.toUpperCase()}</div>
        </button>
      )}

      {loaded && !locked && !rideDone && OFF_TONIGHT.map((r) => (
        <div key={r.key} style={{ ...rideCard('rgba(255,233,196,0.16)'), opacity: 0.35, cursor: 'default' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <span style={{ font: '700 14.5px system-ui' }}>{r.label}</span>
            <span style={{ font: '700 11.5px system-ui' }}>—</span>
          </div>
          <div style={{ font: '600 11px system-ui', opacity: 0.7, marginTop: 2 }}>{r.vehicle}</div>
          <div style={{ font: '600 11.5px system-ui', marginTop: 6 }}>{r.note}</div>
        </div>
      ))}
    </div>
  );
}

const rideCard = (accent) => ({
  display: 'block', width: '100%', textAlign: 'left', marginBottom: 10,
  padding: '11px 13px', borderRadius: 14, cursor: 'pointer',
  border: `1px solid ${accent}`, background: 'rgba(255,255,255,0.05)', color: '#ffe9c4',
});
